/**
 * Domain Registry Graph Transformation
 *
 * Transforms domain-registry catalog data into graph visualization format
 * for graph explorers.
 */
import { DOMAIN_CATALOG, EDGE_CATALOG } from '@unwritten/domain-registry';
/**
 * Transforms domain registry data into GraphData format for visualization
 *
 * Creates nodes for all 20+ domains and edges for all 50+ relationships
 * with complete metadata for detailed inspection.
 */
export function domainRegistryToGraphData() {
    const nodes = [];
    const edges = [];
    const addedNodes = new Set();
    // Transform DOMAIN_CATALOG into graph nodes
    DOMAIN_CATALOG.forEach((domain) => {
        // Skip parent domains without tables (they're organizational only)
        if (domain.tables.length === 0) {
            return;
        }
        const nodeId = domain.id;
        if (!addedNodes.has(nodeId)) {
            nodes.push({
                id: nodeId,
                type: nodeId,
                label: domain.name,
                color: domain.color,
                size: calculateNodeSize(domain.id),
                icon: domain.icon, // Add icon at top level for easier access
                properties: {
                    type: 'domain-registry',
                    domainId: domain.id,
                    category: domain.category,
                    description: domain.description,
                    icon: domain.icon,
                    plural: domain.plural,
                    tables: domain.tables,
                    parent: domain.parent,
                    metadata: domain.metadata,
                    // Count relationships
                    outgoingEdges: EDGE_CATALOG.filter(e => e.from === domain.id).length,
                    incomingEdges: EDGE_CATALOG.filter(e => e.to === domain.id).length,
                },
            });
            addedNodes.add(nodeId);
        }
    });
    // Transform EDGE_CATALOG into graph edges
    EDGE_CATALOG.forEach((edge, idx) => {
        // Only include edges where both domains have nodes
        if (addedNodes.has(edge.from) && addedNodes.has(edge.to)) {
            edges.push({
                id: edge.id || `${edge.from}-${edge.to}-${idx}`,
                source: edge.from,
                target: edge.to,
                type: edge.relationship,
                label: edge.label,
                metadata: {
                    edgeId: edge.id,
                    relationship: edge.relationship,
                    cardinality: edge.cardinality,
                    fieldName: edge.fieldName,
                    fromTable: edge.fromTable,
                    toTable: edge.toTable,
                    bidirectional: edge.bidirectional || false,
                    // Visual properties
                    style: getEdgeStyle(edge.relationship),
                    thickness: getEdgeThickness(edge.cardinality),
                    animated: isAnimatedEdge(edge.relationship),
                },
            });
        }
    });
    return { nodes, edges };
}
/**
 * Calculate node size based on importance (connection count)
 */
function calculateNodeSize(domainId) {
    const outgoingCount = EDGE_CATALOG.filter(e => e.from === domainId).length;
    const incomingCount = EDGE_CATALOG.filter(e => e.to === domainId).length;
    const totalConnections = outgoingCount + incomingCount;
    // Base size + scale by connections
    // Most connected nodes are slightly larger
    return 12 + Math.min(totalConnections * 0.5, 8);
}
/**
 * Get visual style for edge based on relationship type
 */
function getEdgeStyle(relationship) {
    switch (relationship) {
        case 'references':
        case 'referenced_by':
        case 'stored_in':
            return 'solid';
        case 'modifies':
        case 'modified_by':
        case 'generates':
        case 'executes':
        case 'uses':
        case 'analyzes':
            return 'dashed';
        default:
            return 'solid';
    }
}
/**
 * Get edge thickness based on cardinality
 */
function getEdgeThickness(cardinality) {
    switch (cardinality) {
        case 'one-to-one':
            return 3;
        case 'many-to-many':
            return 2;
        case 'one-to-many':
        case 'many-to-one':
        default:
            return 1;
    }
}
/**
 * Determine if edge should be animated based on relationship type
 */
function isAnimatedEdge(relationship) {
    return ['modifies', 'modified_by', 'generates', 'executes'].includes(relationship);
}
/**
 * Get statistics about the domain registry graph
 */
export function getDomainRegistryStats() {
    const domains = DOMAIN_CATALOG.filter(d => d.tables.length > 0);
    const edges = EDGE_CATALOG;
    // Count by category
    const byCategory = domains.reduce((acc, domain) => {
        acc[domain.category] = (acc[domain.category] || 0) + 1;
        return acc;
    }, {});
    // Count by relationship type
    const byRelationship = edges.reduce((acc, edge) => {
        acc[edge.relationship] = (acc[edge.relationship] || 0) + 1;
        return acc;
    }, {});
    // Most connected domains
    const connectionCounts = new Map();
    edges.forEach(edge => {
        connectionCounts.set(edge.from, (connectionCounts.get(edge.from) || 0) + 1);
        connectionCounts.set(edge.to, (connectionCounts.get(edge.to) || 0) + 1);
    });
    const mostConnected = Array.from(connectionCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([domainId, count]) => ({
        domain: DOMAIN_CATALOG.find(d => d.id === domainId)?.name || domainId,
        connections: count,
    }));
    return {
        totalDomains: domains.length,
        totalEdges: edges.length,
        byCategory,
        byRelationship,
        mostConnected,
    };
}
